import { saveAs } from 'file-saver';

import {
  extractCloudinaryCloudName,
  extractCloudinaryPublicId,
  getInfoCloudinary,
  isCloudinary,
  transformCloudinaryImage,
} from 'utils/cloudinary-ts';
import { ITransformationOptions, FetchFormatMode } from 'utils/image-models';
import {
  extractImgixCloudName,
  extractImgixPublicId,
  getInfoImgix,
  isImgix,
  transformImgixImage,
} from 'utils/imgix-ts';
import { cleanUrlString } from 'utils/urlUtils';
import { isBlank } from 'utils/helpers-ts';

/**
 * Transforms an image URL by applying specified transformation options.
 * Detects whether the URL is from Cloudinary or Imgix and calls the appropriate utility.
 *
 * @param url - The original image URL (optional)
 * @param options - Transformation options like width, height, crop, etc.
 * @param decodeUrl - If true, decodes URL-encoded characters in the path before rebuilding
 * @returns The transformed URL or the original URL if no service matches or URL is empty.
 */
export const transformImage = (url?: string, options?: ITransformationOptions, decodeUrl?: boolean): string => {
  if (!url) return '';

  let processedUrl = url;

  // Decode URL-encoded characters if decodeUrl is set
  if (decodeUrl) {
    try {
      processedUrl = decodeURIComponent(url);
    } catch {
      // If decoding fails, use original url
    }
  }

  if (isCloudinary(processedUrl)) {
    return transformCloudinaryImage(processedUrl, options);
  }

  if (isImgix(processedUrl)) {
    return transformImgixImage(processedUrl, options);
  }

  return url;
};

/**
 * Removes width and height parameters from an image URL.
 * Detects whether the URL is from Cloudinary or Imgix and calls the appropriate utility.
 *
 * @param url - The original image URL (optional)
 * @param autoFormat - If true, applies automatic format conversion after removing sizes
 * @returns The URL without size parameters or the original URL if no service matches or URL is empty.
 */
export const removeSizes = (url?: string, autoFormat?: boolean): string => {
  if (!url) return '';

  return transformImage(url, {
    width: null,
    height: null,
    crop: null,
    fetch_format: autoFormat ? 'auto' : undefined,
  });
};

/**
 * Extracts the cloud name from an image URL.
 * Detects whether the URL is from Cloudinary or Imgix and calls the appropriate utility.
 *
 * @param url - The image URL to parse
 * @returns The extracted cloud name, or `undefined` if not found or URL is empty
 */
export const extractImageCloudName = (url?: string): string | undefined => {
  if (!url) return undefined;

  if (isCloudinary(url)) {
    return extractCloudinaryCloudName(url);
  }

  if (isImgix(url)) {
    return extractImgixCloudName(url);
  }

  return undefined;
};

/**
 * Extracts the public ID from an image URL.
 * Detects whether the URL is from Cloudinary or Imgix and calls the appropriate utility.
 *
 * @param url - The image URL to parse
 * @returns The extracted public ID, or `undefined` if not found or URL is empty
 */
export const extractImagePublicId = (url?: string): string | undefined => {
  if (!url) return undefined;

  if (isCloudinary(url)) {
    return extractCloudinaryPublicId(url);
  }

  if (isImgix(url)) {
    return extractImgixPublicId(url);
  }

  return undefined;
};

/**
 * Extracts the producer folder from an image URL.
 * The producer folder is always the first subfolder in the public ID path.
 * Returns `undefined` if the public ID has no subfolder (file at root level).
 *
 * @param url - The image URL to parse
 * @returns The first subfolder name, or `undefined` if not found
 *
 * @example
 * extractImageProducerFolder("https://res.cloudinary.com/demo/image/upload/producer-123/photo.jpg");
 * // Returns: "producer-123"
 *
 * @example
 * extractImageProducerFolder("https://my-source.imgix.net/photo.jpg");
 * // Returns: undefined (no subfolder)
 */
export const extractImageProducerFolder = (url?: string): string | undefined => {
  const publicId = extractImagePublicId(url);
  if (!publicId) return undefined;

  const segments = publicId.split('/');
  if (segments.length < 2) return undefined;

  return segments[0] || undefined;
};

/**
 * Extracts the image category from an image URL.
 * The category is the second subfolder in the public ID path,
 * typically "Profile" or "Products".
 * Returns `undefined` if the public ID has fewer than two subfolders.
 *
 * @param url - The image URL to parse
 * @returns The category (second subfolder), or `undefined` if not found
 *
 * @example
 * extractImageCategory("https://res.cloudinary.com/demo/image/upload/producer-123/Products/photo.jpg");
 * // Returns: "Products"
 *
 * @example
 * extractImageCategory("https://my-source.imgix.net/producer-123/photo.jpg");
 * // Returns: undefined (no second subfolder)
 */
export const extractImageCategory = (url?: string): string | undefined => {
  const publicId = extractImagePublicId(url);
  if (!publicId) return undefined;

  const segments = publicId.split('/');
  if (segments.length < 3) return undefined;

  return segments[1] || undefined;
};

/**
 * Resizes an image URL with specified width and height.
 * A convenience helper around transformImage.
 *
 * @param url - The original image URL (optional)
 * @param width - Target width in pixels (optional)
 * @param height - Target height in pixels (optional)
 * @param fetchFormat - Target format (e.g., 'webp', 'auto') (optional, Cloudinary only)
 * @param disableEnhance - If true, disables auto-enhancement (optional)
 * @param compress - If true, enables auto compression (optional)
 * @returns The resized image URL.
 */
export const resizeImage = (
  url?: string,
  width?: number,
  height?: number,
  fetchFormat: FetchFormatMode = 'auto',
  disableEnhance?: boolean,
  compress?: boolean
): string => {
  return transformImage(url, { width, height, fetch_format: fetchFormat, disableEnhance, compress });
};

/**
 * Resizes an image to 64x64 pixels.
 *
 * @param url - The original image URL (optional)
 * @returns The 64x64 resized image URL.
 */
export const resize64Image = (url?: string): string => resizeImage(url, 64, 64, 'auto', true, true);

/**
 * Resizes an image to 128x128 pixels.
 *
 * @param url - The original image URL (optional)
 * @returns The 128x128 resized image URL.
 */
export const resize128Image = (url?: string): string => resizeImage(url, 128, 128, 'auto');

/**
 * Resizes an image to 128x128 pixels with compression enabled.
 *
 * @param url - The original image URL (optional)
 * @returns The 128x128 resized and compressed image URL.
 */
export const resize128ImageWithCompression = (url?: string): string => resizeImage(url, 128, 128, 'auto', true, true);

/**
 * Resizes an image to 256x256 pixels.
 *
 * @param url - The original image URL (optional)
 * @returns The 256x256 resized image URL.
 */
export const resize256Image = (url?: string): string => resizeImage(url, 256, 256, 'auto', true, true);

/**
 * Resizes an image to 512x512 pixels.
 *
 * @param url - The original image URL (optional)
 * @returns The 512x512 resized image URL.
 */
export const resize512Image = (url?: string): string => resizeImage(url, 512, 512, 'auto');

/**
 * Applies automatic format conversion to an image URL without resizing.
 * Uses 'auto' fetch format (Cloudinary: f_auto, Imgix: auto=format).
 *
 * @param url - The original image URL (optional)
 * @returns The image URL with auto format applied.
 */
export const setAutoFormat = (url?: string): string => transformImage(url, { fetch_format: 'auto' });

/**
 * Extracts the filename from a URL.
 * Returns undefined if extraction fails.
 *
 * @param urlString - The URL to extract filename from
 * @returns The filename or undefined
 */
export const getFilename = (urlString: string): string | undefined => {
  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost';
    const url = new URL(urlString, base);
    const pathname = url.pathname;
    // Filter out empty strings to handle trailing slashes and double slashes
    const parts = pathname.split('/').filter(Boolean);
    const extracted = parts.pop();

    if (extracted) {
      return extracted;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
};

/**
 * Downloads a file from a URL.
 * Uses fetch to get a blob to ensure download triggers even for cross-origin resources,
 * and uses file-saver to save it.
 *
 * @param url - The URL to download
 * @param filename - The filename to save as (optional, extracts from URL if not provided)
 */
export const downloadUrl = async (url: string, filename?: string): Promise<void> => {
  let name = filename;

  if (isBlank(name)) {
    const extracted = getFilename(url);
    if (extracted) {
      try {
        name = decodeURIComponent(extracted);
      } catch (e) {
        console.warn('Failed to decode filename', e);
        name = extracted;
      }
    }
  }

  const finalName = cleanUrlString(name) || 'download';

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    saveAs(blob, finalName);
  } catch (error) {
    console.warn('Failed to fetch blob, falling back to direct saveAs', error);
    saveAs(url, finalName);
  }
};

export interface ImageInfo {
  width: number;
  height: number;
  filesize?: number;
}

/**
 * Get info about image (Cloudinary or Imgix)
 *
 * @param url - The image URL
 * @returns info object with width and height or undefined
 */
export const getInfo = async (url?: string): Promise<ImageInfo | undefined> => {
  if (!url) return undefined;

  let processedUrl = url;

  if (isCloudinary(processedUrl)) {
    const result = await getInfoCloudinary(processedUrl);
    if (result?.output?.width && result?.output?.height) {
      return {
        width: result.output.width,
        height: result.output.height,
        filesize: result.output.bytes,
      };
    }
  }

  if (isImgix(processedUrl)) {
    const result = await getInfoImgix(processedUrl);
    if (result?.PixelWidth && result?.PixelHeight) {
      return {
        width: result.PixelWidth,
        height: result.PixelHeight,
        filesize: result['Content-Length'] ? parseInt(result['Content-Length'], 10) : undefined,
      };
    }
  }

  return undefined;
};

export const isImageName = (name: string) => {
  return /\.(jpe?g|png|webp|tiff?)$/i.test(name);
};

export const isImageUrl = (url: string) => {
  const fileName = getFilename(url);
  return !!fileName && isImageName(fileName);
};
